import React, { useCallback, useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { Container } from "react-bootstrap"
import { getJobServicesDrafting, getDraftsmenList } from "requesters/jobServiceRequester"
import { getDraftmen } from "requesters/userRequester"
import { getFilteredProducts } from "requesters/productRequester"
import CollapsibleSection from "components/CollapsibleSection"
import {
  DRAFTING_OVERSEAS_MANAGER,
  DRAFTING_OVERSEAS,
  DRAFTING_MANAGER,
  DRAFTING_3D_MANAGER,
  TO_OVERSEAS_DRAFTING,
  DRAFTING_OVERSEAS_USER,
  DRAFTING_3D_OVERSEAS_LEBANON_MANAGER,
  DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER,
  DRAFTING_OVERSEAS_LEBANON,
  DRAFTING_OVERSEAS_PHILIPPINES,
  DRAFTING_3D_OVERSEAS_LEBANON_USER,
  DRAFTING_3D_OVERSEAS_PHILIPPINES_USER,
  DRAFTING_3D,
  DRAFTING,
  AWAITING_CONTROL_SURVEY,
  OVERSEAS_STATE
} from "utils/constants"
import { roleCheck } from "utils/index"
import SearchInput from "components/SearchInput"
import FilterDropdown from "components/FilterDropdown"
import Loader from "components/Loader"
import AnchorButton from "components/AnchorButton"
import UserBlockOutTable from "components/UserBlockOutTable"
import _ from "lodash"
import useJobServicesListRealtime, { applyJobServiceListUrgencyUpdate } from "../../hooks/useJobServicesListRealtime"
import DraftingTable from "./DraftingTable"
import { getBaseUrl, getCurrentRole, getCurrentUser } from "../../reducers"

const teamShouldShowLeave = [DRAFTING, DRAFTING_3D, DRAFTING_OVERSEAS, DRAFTING_OVERSEAS_PHILIPPINES, DRAFTING_OVERSEAS_LEBANON]

export default function DraftingPage() {
  const baseUrl = useSelector(getBaseUrl)
  const currentRole = useSelector(getCurrentRole)
  const currentUser = useSelector(getCurrentUser)
  const systemRole = currentUser.attributes.role
  const currentTeamRole = currentUser.attributes.current_team_role
  const readOnly = !(roleCheck(systemRole, currentTeamRole, DRAFTING_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_OVERSEAS_LEBANON_MANAGER))

  const liveJobsAllowed = () => {
    if (roleCheck(systemRole, currentRole, DRAFTING) || roleCheck(systemRole, currentRole, DRAFTING_3D) || roleCheck(systemRole, currentRole, DRAFTING_OVERSEAS) || roleCheck(systemRole, currentRole, DRAFTING_OVERSEAS_LEBANON) || roleCheck(systemRole, currentRole, DRAFTING_OVERSEAS_PHILIPPINES)) {
      return true
    }
    return false
  }

  if (!liveJobsAllowed()) window.location = "/"

  const [requesting, setRequesting] = useState(true)
  const [jobsDrafting, setJobsDrafting] = useState({})
  const [draftmenOptions, setDraftmenOptions] = useState([])
  const [draftsmenList, setDraftsmenList] = useState([])
  const [loadingDraftsmen, setLoadingDraftsmen] = useState({})
  const jobStatusOptions = [
    ["Drafting Required", "drafting_assignment_required"],
    ["QA Required", "local_qa"],
    ["Overseas", "overseas_drafting_assignment_required"],
    ["Overseas QA", "overseas_qa"],
    ["Report Required", "report_required"],
    ["Drafting Overseas Required", "to_overseas_drafting"],
    ["Local Drafting", "to_local_drafting"],
  ]

  const [jobTypes, setJobTypes] = useState([])

  const isOverseasTeam = currentRole?.includes(DRAFTING_OVERSEAS) || currentRole?.includes(DRAFTING_OVERSEAS_LEBANON) || currentRole?.includes(DRAFTING_OVERSEAS_PHILIPPINES)
  const isOverseasManager = currentTeamRole.includes(DRAFTING_OVERSEAS_MANAGER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_LEBANON_MANAGER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_PHILIPPINES_MANAGER)
  const isOverseasUser = currentTeamRole.includes(DRAFTING_OVERSEAS_USER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_LEBANON_USER) || currentTeamRole.includes(DRAFTING_3D_OVERSEAS_PHILIPPINES_USER)
  const isLocalDrafting = currentUser.attributes.default_team === "drafting"
  const is2DTeam = currentRole.includes(DRAFTING)
  const isManagerRole = roleCheck(systemRole, currentTeamRole, DRAFTING_MANAGER) || roleCheck(systemRole, currentTeamRole, DRAFTING_3D_MANAGER) || isOverseasManager

  const byDraftsmanFilter = () => {
    if (isOverseasManager) {
      return undefined
    }
    if (readOnly) {
      return currentUser.id
    }
    return undefined
  }

  const initialFilters = {
    by_status: (isOverseasUser && !isLocalDrafting) ? TO_OVERSEAS_DRAFTING : undefined,
    by_draftman: byDraftsmanFilter(),
    by_job_type: undefined,
    by_keyword: undefined
  }

  const [filters, setFilters] = useState(initialFilters)
  const [isFilteredByDropDown, setIsFilteredByDropDown] = useState(false)
  const byStatusName = _.get(jobStatusOptions, `${_.findIndex(jobStatusOptions, option => option[1] === filters.by_status)}.0`)
  const byJobTypeName = _.get(jobTypes, `${_.findIndex(jobTypes, type => type[1] === filters.by_job_type)}.0`)
  const byDraftmenName = _.get(draftmenOptions, `${_.findIndex(draftmenOptions, option => option[1] === filters.by_draftman)}.0`)
  const filtersApplied = Object.entries(filters).some(([key, val]) => key !== "by_draftman" && val !== initialFilters[key]);

  const clearFilters = () => {
    setFilters(initialFilters)
    setIsFilteredByDropDown(true)
  }

  const setByStatus = val => {
    setFilters({ ...filters, by_status: val })
    setIsFilteredByDropDown(true)
  }

  const setByJobType = val => {
    setFilters({ ...filters, by_job_type: val })
    setIsFilteredByDropDown(true)
  }

  const setBySearch = val => {
    setFilters({ ...filters, by_keyword: val })
  }

  const setByDraftmen = val => {
    setFilters({ ...filters, by_draftman: val })
    setIsFilteredByDropDown(true)
  }

  useEffect(() => {
    setFilters({ ...filters })
  }, [])

  const fetchDraftsmenList = () => {
    setRequesting(true)
    getDraftsmenList(baseUrl, filters)
      .then(res => {
        if (res.status === 200) {
          const list = _.get(res, "response", [])
          setDraftsmenList(list)
          setRequesting(false)
        }
      })
      .catch(() => {
        setRequesting(false)
      })
  }

  const fetchDataForDraftsman = useCallback((draftsmanKey, draftsmanId, { silent } = {}) => {
    if (!silent) setLoadingDraftsmen(prev => ({ ...prev, [draftsmanKey]: true }))
    // Build filter based on category type
    let draftsmanFilter = { ...filters }

    if (draftsmanKey === "Awaiting Control Survey") {
      draftsmanFilter = { ...filters, by_status: AWAITING_CONTROL_SURVEY, by_draftman: undefined }
    } else if (draftsmanKey === "Unassigned") {
      draftsmanFilter = { ...filters, by_draftman: undefined, unassigned: true }
    } else if (draftsmanKey === "Unassigned Overseas") {
      draftsmanFilter = { ...filters, by_status: OVERSEAS_STATE, by_draftman: undefined }
    } else {
      draftsmanFilter = { ...filters, by_draftman: draftsmanId }
    }

    getJobServicesDrafting(baseUrl, draftsmanFilter)
      .then(res => {
        if (res.status === 200) {
          const jobServices = _.get(res, "response", {})
          const matchingData = Object.values(jobServices).flat()
          setJobsDrafting(prev => ({
            ...prev,
            [draftsmanKey]: matchingData || []
          }))
          setLoadingDraftsmen(prev => ({ ...prev, [draftsmanKey]: false }))
        }
      })
      .catch(() => {
        setLoadingDraftsmen(prev => ({ ...prev, [draftsmanKey]: false }))
      })
  }, [baseUrl, filters])

  const jobsDraftingRef = useRef(jobsDrafting)
  const draftsmenListRef = useRef(draftsmenList)
  jobsDraftingRef.current = jobsDrafting
  draftsmenListRef.current = draftsmenList

  const refreshLoadedDraftingLists = useCallback(() => {
    Object.keys(jobsDraftingRef.current || {}).forEach(draftsmanKey => {
      const draftsman = draftsmenListRef.current.find(item => (item.key || item.name) === draftsmanKey)
      fetchDataForDraftsman(draftsmanKey, draftsman?.id, { silent: true })
    })
  }, [fetchDataForDraftsman])

  useJobServicesListRealtime({
    onUpdate: payload => {
      setJobsDrafting(prev => applyJobServiceListUrgencyUpdate(prev, payload))
    },
    onVisible: refreshLoadedDraftingLists
  })

  const getDraftmenHandler = () => {
    setRequesting(true)
    getDraftmen(baseUrl).then(res => {
      if (res.status === 200) {
        const newData = res.response.map(item => {
          const { label, value, ...other } = item
          return [`${label}`, `${value}`, { ...other }]
        })
        setDraftmenOptions(newData)
      }
    })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
    setRequesting(false)
  }

  useEffect(() => {
    getFilteredProducts(baseUrl, "drafting")
      .then(res => {
        if (res.status === 200) {
          const products = Object.values(_.get(res.response, "product", {}))
          const allJobTypes = products.map(js => ([js.attributes.name, js.id]))
          setJobTypes(allJobTypes)
        }
      })
      // eslint-disable-next-line no-console
      .catch(err => console.log(err))
  }, [])

  const handleSubmit = e => {
    e.preventDefault()
    setIsFilteredByDropDown(true)
  }

  useEffect(() => {
    fetchDraftsmenList()
    getDraftmenHandler()
    setJobsDrafting({}) // Clear loaded data when filters change
    setIsFilteredByDropDown(false)
  }, [isFilteredByDropDown, currentRole])

  const UserBlockOutSection = useCallback(() => (
    <div className="users-block-outs-section mb-2">
      <UserBlockOutTable byTeams={teamShouldShowLeave} />
    </div>
  ), [teamShouldShowLeave])

  return (
    <Container fluid className="">
      <div className="container-inner">
        <div className="d-flex justify-content-between align-items-center header-tabs-container">
          <div className="header-tabs">
            <h1>Drafting</h1>
          </div>
        </div>
        <div className="table-listing-filters d-md-flex justify-content-between">
          <div>
            <div className="btn-group mr-2">
              <SearchInput value={filters.by_keyword} setValue={setBySearch} handleSubmit={handleSubmit} disableSubmit={requesting} />
            </div>
            {!isOverseasTeam && (
              <div className="btn-group mr-2">
                <FilterDropdown title="Status" filteredName={byStatusName} setFilter={setByStatus} options={jobStatusOptions} />
              </div>
            )}
            <div className="btn-group mr-2">
              <FilterDropdown title="Job Service" filteredName={byJobTypeName} setFilter={setByJobType} options={jobTypes} />
            </div>
            {(isOverseasManager || !readOnly) && (
              <div className="btn-group mr-2">
                <FilterDropdown title="Draftsman" filteredName={byDraftmenName} setFilter={setByDraftmen} options={draftmenOptions} />
              </div>
            )}
            <div className="btn-group">
              <AnchorButton
                className="f-small"
                onClick={clearFilters}>
                Clear Filters
              </AnchorButton>
            </div>
          </div>
        </div>

        { isManagerRole && UserBlockOutSection() }

        {requesting ? <Loader />
          : (
            <JobServiceByDraftsmanTables
              jobsDrafting={jobsDrafting}
              setJobsDrafting={setJobsDrafting}
              draftmenOptions={draftmenOptions}
              draftsmenList={draftsmenList}
              fetchDataForDraftsman={fetchDataForDraftsman}
              loadingDraftsmen={loadingDraftsmen}
              is2DTeam={is2DTeam}
              filtersApplied={filtersApplied}
              readOnly={readOnly} />
          )}
      </div>
    </Container>
  )
}


const JobServiceByDraftsmanTables = ({
  readOnly,
  filtersApplied,
  draftmenOptions,
  jobsDrafting,
  setJobsDrafting,
  draftsmenList,
  fetchDataForDraftsman,
  loadingDraftsmen,
  is2DTeam
}) => {
  const currentUser = useSelector(getCurrentUser)
  const [expandedDraftsmen, setExpandedDraftsmen] = useState(new Set())
  const [initialLoadDone, setInitialLoadDone] = useState(false)

  const sortedDraftsmenList = [...draftsmenList].sort((a, b) => {
    const priority = ["Awaiting Control Survey", "Unassigned", "Unassigned Overseas"]
    const aIndex = priority.indexOf(a.name)
    const bIndex = priority.indexOf(b.name)
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
  })

  // Auto-expand and load "Unassigned", "Unassigned Overseas", and current user's section on initial load
  useEffect(() => {
    if (!initialLoadDone && sortedDraftsmenList.length > 0) {
      const defaultExpanded = new Set()
      sortedDraftsmenList.forEach(({ name, id, key }) => {
        const shouldExpand = name === "Unassigned" || name === "Unassigned Overseas" || currentUser.id === id?.toString()

        if (shouldExpand) {
          const draftsmanKey = key || name
          defaultExpanded.add(draftsmanKey)
          // Fetch data for these sections
          if (!jobsDrafting[draftsmanKey]) {
            fetchDataForDraftsman(draftsmanKey, id)
          }
        }
      })
      setExpandedDraftsmen(defaultExpanded)
      setInitialLoadDone(true)
    }
  }, [sortedDraftsmenList, initialLoadDone, jobsDrafting, fetchDataForDraftsman])

  const toggleDraftsman = (draftsmanKey, draftsmanId) => {
    const newExpanded = new Set(expandedDraftsmen)
    if (newExpanded.has(draftsmanKey)) {
      newExpanded.delete(draftsmanKey)
    } else {
      newExpanded.add(draftsmanKey)
      // Fetch data if not already loaded
      if (!jobsDrafting[draftsmanKey]) {
        fetchDataForDraftsman(draftsmanKey, draftsmanId)
      }
    }
    setExpandedDraftsmen(newExpanded)
  }

  const refetchDraftsmanData = (draftsmanKey, draftsmanId) => {
    fetchDataForDraftsman(draftsmanKey, draftsmanId)
  }

  return (
    <div className="drafting-container my-5">
      {!!sortedDraftsmenList?.length && sortedDraftsmenList.map(({
        id, name, count, key
      }) => {
        const draftsmanKey = key || name
        const isExpanded = expandedDraftsmen.has(draftsmanKey)
        const jobServicesDraftsman = jobsDrafting[draftsmanKey]
        const isLoading = loadingDraftsmen[draftsmanKey]

        return (
          <CollapsibleSection
            key={`draftsman-${draftsmanKey}`}
            itemKey={draftsmanKey}
            name={name}
            count={count}
            isExpanded={isExpanded}
            isLoading={isLoading}
            onToggle={() => toggleDraftsman(draftsmanKey, id)}
            onRefresh={() => refetchDraftsmanData(draftsmanKey, id)}>
            {jobServicesDraftsman?.length > 0 ? (
              <DraftingTable
                jobsDrafting={jobServicesDraftsman}
                setJobsDrafting={updatedJobs => {
                  setJobsDrafting(prev => ({
                    ...prev,
                    [draftsmanKey]: updatedJobs
                  }))
                }}
                draftmenOptions={draftmenOptions}
                fetchData={() => refetchDraftsmanData(draftsmanKey, id)}
                is2DTeam={is2DTeam}
                filtersApplied={filtersApplied}
                readOnly={readOnly} />
            ) : (
              <div className="text-center py-4 text-muted">No job services found</div>
            )}
          </CollapsibleSection>
        )
      })}
    </div>
  )
}
